import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../layout/index";
import StepHeader from "./StepHeader";
import { toast } from "react-toastify";
import { APP_ROUTES } from "../../constants/approutes";
import uploadicon from "../../assets/uploadicon.svg";
import FullScreenLoader from "../FullScreenLoader";
import { Eye, Trash2 } from "lucide-react";

// Dropdown options
const drugAllergyList = [
  { label: "Penicillin", value: "Penicillin" },
  { label: "Sulfonamide", value: "Sulfonamide" },
  { label: "ASA", value: "ASA" },
  { label: "NSAIDs", value: "NSAIDs" },
  { label: "Aspirin", value: "Aspirin" },
  { label: "Ibuprofen", value: "Ibuprofen" },
  { label: "Naproxen", value: "Naproxen" },
];
const foodAllergyList = [
  { label: "Egg", value: "Egg" },
  { label: "Peanut", value: "Peanut" },
  { label: "Peanut butter", value: "Peanut butter" },
  { label: "Milk", value: "Milk" },
  { label: "Nuts", value: "Nuts" },
  { label: "Soy", value: "Soy" },
  { label: "Fish", value: "Fish" },
  { label: "Animal meat", value: "Animal meat" },
  { label: "Wheat", value: "Wheat" },
  { label: "Animal Protein", value: "Animal Protein" },
];
const chemicalAllergyList = [
  { label: "Dyes", value: "Dyes" },
  { label: "Colour Additives", value: "Colour Additives" },
  { label: "Cosmetics", value: "Cosmetics" },
  { label: "Soap", value: "Soap" },
  { label: "Moisturiser", value: "Moisturiser" },
  { label: "Perfume", value: "Perfume" },
];
const otherAllergyList = [
  { label: "Animal Dander", value: "Animal Dander" },
  { label: "Insect Bite", value: "Insect Bite" },
  { label: "Insect Sting", value: "Insect Sting" },
  { label: "Pollen", value: "Pollen" },
  { label: "Insect Faeces", value: "Insect Faeces" },
  { label: "House Dust Mites", value: "House Dust Mites" },
  { label: "Mite Faeces", value: "Mite Faeces" },
  { label: "Natural Rubber Latex", value: "Natural Rubber Latex" },
];

const medicalHistoryOptions = [
  "Diabetes",
  "Hypertension",
  "Thyroid",
  "Tuberculosis",
  "Myocardial infarction/ Heart attack",
  "Cholesterol high",
  "Jaundice",
  "Kidney disease",
  "COPD (Chronic Obstructive Pulmonary disease)",
  "Asthma",
  "Cancers",
  "HIV",
  "Hepatitis B",
  "Hepatitis C",
  "Typhoid",
  "Malaria",
  "Dengue",
  "Anemia",
];
const surgicalHistoryOptions = [
  "Cholecystectomy (gall bladder removal)",
  "Appendectomy (appendix removal)",
  "Vasectomy (vas deferens)",
  "Cardiac surgery (open heart)",
  "Bypass surgery - stent in the heart",
  "Hysterectomy (uterus removal)",
  "Hernia surgery",
  "Angioplasty",
  "Caesarean section",
  "Knee replacement",
  "Spine Surgery",
  "Cataract surgery",
  "Laser surgery for retina (eye)",
  "Hip replacement surgery",
  "Cancer surgery",
  "Ear surgery",
  "Tonsillectomy (tonsil removal)",
];

// Category config for allergy chips
const categoryConfig = {
  drug: { name: "Drug" },
  food: { name: "Food" },
  chemical: { name: "Chemical" },
  other: { name: "Other" },
};

// ---- MultiSelectDropdown ----
function MultiSelectDropdown({
  label,
  name,
  value,
  onChange,
  options,
  isAllergy = false,
  allowCustom = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [selectedItems, setSelectedItems] = useState(
    value ? value.split(", ").filter((v) => v.trim()) : []
  );
  const [activeCategory, setActiveCategory] = useState(
    isAllergy ? "drug" : null
  );

  // map each allergy value → its category key
  const allergyCategoryMap = useMemo(() => {
    if (!isAllergy) return {};
    const m = {};
    drugAllergyList.forEach((o) => (m[o.value] = "drug"));
    foodAllergyList.forEach((o) => (m[o.value] = "food"));
    chemicalAllergyList.forEach((o) => (m[o.value] = "chemical"));
    otherAllergyList.forEach((o) => (m[o.value] = "other"));
    return m;
  }, [isAllergy]);

  const getAllergyOptions = () => {
    switch (activeCategory) {
      case "drug":
        return drugAllergyList;
      case "food":
        return foodAllergyList;
      case "chemical":
        return chemicalAllergyList;
      case "other":
        return otherAllergyList;
      default:
        return [];
    }
  };

  const currentOptions = isAllergy
    ? getAllergyOptions()
    : options.map((opt) => ({ label: opt, value: opt }));

  const hasOthersSelected = selectedItems.includes("Others");

  const handleItemSelect = (item) => {
    const next = selectedItems.includes(item.value)
      ? selectedItems.filter((v) => v !== item.value)
      : [...selectedItems, item.value];
    setSelectedItems(next);
    onChange({ target: { name, value: next.join(", ") } });
  };

  const handleCustomAdd = () => {
    const v = customValue.trim();
    if (!v || selectedItems.includes(v)) return;
    const next = [...selectedItems, v];
    setSelectedItems(next);
    onChange({ target: { name, value: next.join(", ") } });
    setCustomValue("");
  };

  const removeItem = (v) => {
    const next = selectedItems.filter((x) => x !== v);
    setSelectedItems(next);
    onChange({ target: { name, value: next.join(", ") } });
  };

  // Function to format text display for allergies
  const formatAllergyText = () => {
    if (!isAllergy || selectedItems.length === 0) return "";

    const categorizedItems = {
      drug: [],
      food: [],
      chemical: [],
      other: [],
    };

    selectedItems.forEach((item) => {
      const category = allergyCategoryMap[item] || "other";
      categorizedItems[category].push(item);
    });

    const parts = [];
    Object.entries(categorizedItems).forEach(([category, items]) => {
      if (items.length > 0) {
        parts.push(`${categoryConfig[category].name} - ${items.join(", ")}`);
      }
    });

    return parts.join(" | ");
  };

  return (
    <div className="flex flex-col relative h-full">
      <label className="text-sm text-slate-700 mb-2 font-semibold flex items-center">
        <div className="w-1 h-4 bg-gradient-to-b from-blue-500 to-teal-500 rounded-full mr-2" />
        {label}
      </label>

      {/* Custom input for non-allergy fields when Others is selected */}
      {hasOthersSelected && !isAllergy && allowCustom && (
        <div className="mb-3 flex gap-2">
          <input
            type="text"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleCustomAdd()}
            placeholder="Please specify..."
            className="flex-1 px-4 py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleCustomAdd}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Add
          </button>
        </div>
      )}

      {/* Trigger box with text display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="group cursor-pointer border-2 border-gray-200 rounded-xl p-3 min-h-[100px] sm:min-h-[120px]
                   flex items-start hover:border-blue-300 transition-shadow shadow-sm"
      >
        <div className="flex-1">
          {selectedItems.length > 0 ? (
            <div className="text-sm text-gray-700">
              {isAllergy ? formatAllergyText() : selectedItems.join(", ")}
            </div>
          ) : (
            <span className="text-gray-400 italic">
              Click to select options...
            </span>
          )}
        </div>

        <div
          className={`ml-auto transform transition-transform duration-200 
                          text-gray-400 group-hover:text-blue-500 ${
                            isOpen ? "rotate-180" : ""
                          }`}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-xl shadow-xl mt-1 max-h-[28rem] overflow-hidden">
            {isAllergy && (
              <div className="p-4 border-b bg-gray-50">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(categoryConfig).map(([cat, cfg]) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition
                        ${
                          activeCategory === cat
                            ? "bg-blue-500 text-white"
                            : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100"
                        }`}
                    >
                      {cfg.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="max-h-80 overflow-y-auto overscroll-contain">
              {/* Options list */}
              <div>
                {currentOptions.map((opt, i) => (
                  <div
                    key={i}
                    onClick={() => handleItemSelect(opt)}
                    className={`flex items-center justify-between px-4 py-3 cursor-pointer
                                ${
                                  selectedItems.includes(opt.value)
                                    ? "bg-blue-50 border-r-4 border-blue-500"
                                    : "hover:bg-blue-50"
                                }`}
                  >
                    <div className="flex items-center">
                      <div
                        className={`w-4 h-4 border-2 mr-3 flex items-center justify-center
                                       ${
                                         selectedItems.includes(opt.value)
                                           ? "bg-blue-500 border-blue-500"
                                           : "border-gray-300 hover:border-blue-400"
                                       }`}
                      >
                        {selectedItems.includes(opt.value) && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        className={
                          selectedItems.includes(opt.value)
                            ? "text-blue-700 font-medium"
                            : "text-gray-700"
                        }
                      >
                        {opt.label}
                      </span>
                    </div>
                    {selectedItems.includes(opt.value) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(opt.value);
                        }}
                        className="w-5 h-5 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Selected items display with remove buttons */}
              {selectedItems.length > 0 && (
                <div className="p-4 border-t bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Items:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        <span>{item}</span>
                        <button
                          onClick={() => removeItem(item)}
                          className="ml-2 w-4 h-4 bg-red-100 rounded-full hover:bg-red-200 text-red-600 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom input section for allergy or when allowCustom is true */}
              {(isAllergy || allowCustom) && (
                <div className="p-4 border-t bg-gray-50 flex gap-2">
                  <input
                    type="text"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleCustomAdd()}
                    placeholder={
                      isAllergy ? "Add custom allergy..." : "Add custom item..."
                    }
                    className="flex-1 px-3 py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleCustomAdd}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>
          </div>
          <div
            className="fixed inset-0 z-10 pointer-events-auto"
            onClick={() => setIsOpen(false)}
            style={{ background: 'transparent' }}
          />
        </>
      )}
    </div>
  );
}

// ---- TextareaField ----
function TextareaField({ label, name, value, onChange }) {
  return (
    <div className="flex flex-col h-full">
      <label className="text-sm text-slate-700 mb-2 font-semibold flex items-center">
        <div className="w-1 h-4 bg-gradient-to-b from-green-500 to-teal-500 rounded-full mr-2" />
        {label}
      </label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder="Enter detailed information here..."
        className="rounded-xl border-2 border-gray-200 px-4 py-3 text-sm shadow-sm hover:shadow-md
                   focus:ring-2 focus:ring-green-500 resize-none min-h-[100px] sm:min-h-[120px]"
      />
    </div>
  );
}

// ---- MedicalhistoryPage ----
export default function EditMedicalHistory({
  editMedical,
  onCancel,
  patientId,
}) {
  const navigate = useNavigate();
  // const { state } = useLocation();
  // const patientCode = state?.patientCode;
  const API_URL = process.env.REACT_APP_API_URL;
  console.log(editMedical, "editMedical");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [medicaldata, setMedicaldata] = useState({
    allergyhistory: editMedical?.allergyHistory || "",
    medicalhistory: editMedical?.medicalHistory || "",
    surgicalhistory: editMedical?.surgicalHistory || "",
    familyhistory: editMedical?.familyHistory || "",
    socialhistory: editMedical?.socialHistory || "",
    medication: editMedical?.medication || "",
  });

  //  useEffect(() => {
  //   if (editMedical) {
  //     setMedicaldata({
  //       allergyhistory: editMedical?.allergyHistory || "",
  //       medicalhistory: editMedical?.medicalHistory || "",
  //       surgicalhistory: editMedical?.surgicalHistory || "",
  //       familyhistory: editMedical?.familyHistory || "",
  //       socialhistory: editMedical?.socialHistory || "",
  //       medication: editMedical?.medication || "",
  //     });
  //   }
  // }, [editMedical]);

  const [reportFile, setReportFile] = useState(null);
  const [reportBase64, setReportBase64] = useState("");
  const [reportType, setReportType] = useState("");
  const [labReportFiles, setLabReportFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // New state variables for existing files
  const [existingPrescription, setExistingPrescription] = useState(null);
  const [existingPrescriptiondata, setExistingPrescriptiondata] = useState(null);
  const [existingLabReports, setExistingLabReports] = useState([]);
  const [existingLabReportsdata, setExistingLabReportsdata] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Load existing files when editing
  useEffect(() => {
    const loadExistingFiles = async () => {
      if (!editMedical?.id) return;

      setLoadingFiles(true);
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");

      try {
        // Load existing prescription
        if (editMedical.documentUrl) {
          const prescriptionRes = await fetch(
            `${API_URL}patient-medical-history/file-url?fileName=${editMedical.documentUrl}`,
            {
              headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
            }
          );
          const prescriptionData = await prescriptionRes.json();
          if (prescriptionData.signedUrl) {
            setExistingPrescription({
              name: "Prescription",
              displayName: "Prescription",
              url: prescriptionData.signedUrl,
              type: editMedical.documentUrl.toLowerCase().endsWith(".pdf")
                ? "application/pdf"
                : "image/*",
              fileName: editMedical.documentUrl,
            });
            setExistingPrescriptiondata({
              name: "Prescription",
              displayName: "Prescription",
              url: prescriptionData.signedUrl,
              type: editMedical.documentUrl.toLowerCase().endsWith(".pdf")
                ? "application/pdf"
                : "image/*",
              fileName: editMedical.documentUrl,
            });
          }
        }

        // Load existing lab reports
        if (editMedical.labReportUrls && editMedical.labReportUrls.length > 0) {
          const labReportPromises = editMedical.labReportUrls.map(
            async (fileName, index) => {
              const labRes = await fetch(
                `${API_URL}patient-medical-history/file-url?fileName=${fileName}`,
                {
                  headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
                }
              );
              const labData = await labRes.json();
              if (labData.signedUrl) {
                return {
                  id: `existing-${index}`,
                  name: fileName,
                  displayName: `Lab Report ${index + 1}`,
                  url: labData.signedUrl,
                  type: fileName.toLowerCase().endsWith(".pdf")
                    ? "application/pdf"
                    : "image/*",
                  fileName: fileName,
                  isExisting: true,
                };
              }
              return null;
            }
          );

          const labResults = await Promise.all(labReportPromises);
          const validLabResults = labResults.filter(
            (result) => result !== null
          );
          setExistingLabReports(validLabResults);
          setExistingLabReportsdata(validLabResults);
        }
      } catch (error) {
        console.error("Error loading existing files:", error);
        toast.error("Failed to load existing files");
      } finally {
        setLoadingFiles(false);
      }
    };

    loadExistingFiles();
  }, [editMedical, API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMedicaldata((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const b64 = reader.result.split(",")[1];
      setReportBase64(b64);
      setReportType(file.type.split("/")[1]);
      setReportFile(file); // 👉 Keep the original file here
    };
    reader.readAsDataURL(file);
  };

  const handleLabReportUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newLab = [];
    const currentCount = labReportFiles.length + existingLabReports.length;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const reader = new FileReader();
      const data = await new Promise((resolve) => {
        reader.onloadend = () => {
          resolve({
            id: Date.now() + Math.random(),
            file,
            name: file.name,
            displayName: `Lab Report ${currentCount + i + 1}`,
            size: file.size,
            type: file.type,
            url: reader.result,
            base64: reader.result.split(",")[1],
          });
        };
        reader.readAsDataURL(file);
      });
      newLab.push(data);
    }
    setLabReportFiles((prev) => [...prev, ...newLab]);
  };

  const removeLabReport = (id) => {
    // Convert id to string to handle both string and numeric IDs
    const idStr = String(id);
    // Check if it's an existing lab report
    if (idStr.startsWith("existing-")) {
      setExistingLabReports((prev) => prev.filter((r) => r.id !== id));
    } else {
      setLabReportFiles((prev) => prev.filter((r) => r.id !== id));
    }
  };
  const removePrescription = () => {
    setReportFile(null);
    setReportBase64("");
    setReportType("");
    setExistingPrescription(null);
  };

  const openPreview = (file, isPrescription = false) => {
    if (isPrescription) {
      // Handle existing prescription
      if (existingPrescription) {
        setPreviewFile({
          name: existingPrescription.displayName,
          url: existingPrescription.url,
          type: existingPrescription.type,
        });
      } else if (reportFile && reportBase64) {
        const type = reportFile?.type || "application/pdf";
        setPreviewFile({
          name: "Prescription",
          url: `data:${type};base64,${reportBase64}`,
          type: type,
        });
      }
    } else {
      setPreviewFile({ ...file, name: file.displayName || file.name });
    }
    setShowPreview(true);
  };

  const closePreview = () => {
    setPreviewFile(null);
    setShowPreview(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024,
      sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleNext = async () => {
    if (!editMedical?.patientCode && !patientId) {
      toast.error("Patient code missing.");
      return;
    }

    // Check if all medical history fields are empty (skipped)
    const medFields = [
      medicaldata.allergyhistory,
      medicaldata.medicalhistory,
      medicaldata.surgicalhistory,
      medicaldata.familyhistory,
      medicaldata.socialhistory,
      medicaldata.medication,
      reportFile || existingPrescription,
      ...(labReportFiles || []),
      ...(existingLabReports || []),
    ];
    const allMedEmpty = medFields.every(
      (v) => !v || (Array.isArray(v) && v.length === 0)
    );
    // if (allMedEmpty) {
    //   toast.info("Medical History Skipped");
    //   setIsLoggingIn(false);
    //   navigate(APP_ROUTES.SLOTBOOKING, { state: { patientCode } });
    //   return;
    // }

    setIsLoggingIn(true);
    const payload = {
      patientCode: editMedical?.patientCode || patientId,
      allergyHistory: medicaldata.allergyhistory,
      medicalHistory: medicaldata.medicalhistory,
      surgicalHistory: medicaldata.surgicalhistory,
      familyHistory: medicaldata.familyhistory,
      socialHistory: medicaldata.socialhistory,
      medication: medicaldata.medication,
    };

    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = localStorage.getItem("tenantId");
      // Check if medical history already exists (has an ID)
      if (editMedical?.id) {
        console.log(reportFile,labReportFiles.length,existingPrescription, existingLabReports,"erdtfyguiguy")
        // Check if there are any file changes
        // const hasFileChanges =
        //   reportFile ||
        //   labReportFiles.length > 0 ||
        //   existingPrescription === null ||
        //   existingLabReports.length === 0;

        // if (hasFileChanges) {
        //   // If there are file changes, create a new medical history entry
        //   // This is because the backend might not support file updates through the update endpoint
        //   const formData = new FormData();
        //   formData.append("history", JSON.stringify(payload));

        //   // Add new prescription file if uploaded
        //   if (reportFile) {
        //     formData.append("file", reportFile);
        //   }

        //   // Add new lab report files if uploaded
        //   labReportFiles.forEach((lr) =>
        //     formData.append("labReports", lr.file)
        //   );

        //   const res = await fetch(`${API_URL}patient-medical-history/add`, {
        //     method: "POST",
        //     headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
        //     body: formData,
        //   });
        //   const result = await res.json();
        //   console.log(result, "add result");
        //   localStorage.setItem("medicaldata", JSON.stringify(result.data));

        //   if (res.ok) {
        //     toast.success("Medical history updated successfully!");
        //     // Trigger a page refresh to show updated data
        //     // window.location.reload();
        //     onCancel();
        //   } else {
        //     toast.error(result.message || "Update failed.");
        //   }
        // } else {
          const formData = new FormData();
          formData.append("history", JSON.stringify(payload));
         
          if (!reportFile && !existingPrescription) {
            // formData.append("file", "null");
          } else {
            if (reportFile) formData.append("file", reportFile);
            if (existingPrescription) formData.append("file", existingPrescription.fileName);
          }
          
          if (labReportFiles.length === 0 && (!existingLabReports || existingLabReports.length === 0)) {
            formData.append("labReports", "null");
          } else {
            labReportFiles.forEach((lr) => formData.append("labReports", lr.file));
            existingLabReports?.forEach((elr) => {
              if (elr?.fileName) formData.append("labReports", elr.fileName);
            });
          }
          const removedReports = existingLabReportsdata.filter(
            dataItem =>
              !existingLabReports.some(
                existingItem => existingItem.fileName === dataItem.fileName
              )
          );
          
          // ✅ Append each missing report’s fileName to FormData
          removedReports.forEach(item => {
            formData.append("removeLabReportUrls", item.fileName);
          });
          if (existingPrescriptiondata && !existingPrescription) {
            formData.append("removeDocument", "true");
          }


          // If no file changes, just update the text fields
          console.log(existingPrescriptiondata,existingPrescription, "formData");
          let url = `${API_URL}patient-medical-history/update/${editMedical.id}`;
          if (existingPrescriptiondata && !existingPrescription) {
            url += '?removeDocument=true'; // or whatever param you need
          }

          // if (existingLabReportsdata && !existingLabReports) {
          //   url += '?removeDocument=true'; // or whatever param you need
          // }

         console.log(existingLabReportsdata,existingLabReports, "existingLabReportsdata");
          const res = await fetch(
            url,
            {
              method: "PUT",
              headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
              body: formData,
            }
          );
          const result = await res.json();
          console.log(result, "update result");
          localStorage.setItem("medicaldata", JSON.stringify(result.data));

          if (res.ok) {
            toast.success("Medical history updated successfully!");
            // Trigger a page refresh to show updated data
            window.location.reload();
            onCancel();
          } else {
            toast.error(result.message || "Update failed.");
          }
      
      } else {
        // Add new medical history
        const formData = new FormData();
        formData.append("history", JSON.stringify(payload));
        if (reportFile) formData.append("file", reportFile);
        labReportFiles.forEach((lr) => formData.append("labReports", lr.file));

        const res = await fetch(`${API_URL}patient-medical-history/add`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, tenantId: tenantId },
          body: formData,
        });
        const result = await res.json();
        console.log(result, "add result");
        localStorage.setItem("medicaldata", JSON.stringify(result.data));

        if (res.ok) {
          toast.success("Medical history saved successfully!");
          // Trigger a page refresh to show updated data
          window.location.reload();
          onCancel();
        } else {
          toast.error(result.message || "Save failed.");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };
  console.log(medicaldata, "medicaldataaaa");

  return (
    <>
      {isLoggingIn && <FullScreenLoader message="Saving Medical History..." />}
      {loadingFiles && <FullScreenLoader message="Loading existing files..." />}

      {showPreview && previewFile && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] rounded-2xl overflow-auto shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold">{previewFile.name}</h3>
              <button onClick={closePreview} className="p-2">
                ✕
              </button>
            </div>
            <div className="p-6">
              {previewFile.type ? (
                previewFile.type.startsWith("image/") ? (
                  <img src={previewFile.url} className="max-w-full" />
                ) : previewFile.type === "application/pdf" ? (
                  <iframe src={previewFile.url} className="w-full h-96" />
                ) : (
                  <p className="text-center">No preview available</p>
                )
              ) : (
                <p className="text-center">No preview available</p>
              )}
            </div>
          </div>
        </div>
      )}

      <>
        <div className="min-h-screen px-2 sm:px-4 md:pt-10">
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 lg:gap-8">
            {/* Main Form */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <MultiSelectDropdown
                label="Any Allergy History?"
                name="allergyhistory"
                value={medicaldata.allergyhistory}
                onChange={handleChange}
                options={[]}
                isAllergy={true}
              />
              <MultiSelectDropdown
                label="Any Medical History?"
                name="medicalhistory"
                value={medicaldata.medicalhistory}
                onChange={handleChange}
                options={medicalHistoryOptions}
                allowCustom={true}
              />
              <MultiSelectDropdown
                label="Any Surgical History?"
                name="surgicalhistory"
                value={medicaldata.surgicalhistory}
                onChange={handleChange}
                options={surgicalHistoryOptions}
                allowCustom={true}
              />
              <TextareaField
                label="Any Family History?"
                name="familyhistory"
                value={medicaldata.familyhistory}
                onChange={handleChange}
              />
              <TextareaField
                label="Any Social History?"
                name="socialhistory"
                value={medicaldata.socialhistory}
                onChange={handleChange}
              />
              <TextareaField
                label="Any Medication?"
                name="medication"
                value={medicaldata.medication}
                onChange={handleChange}
              />
            </div>

            {/* Uploads */}
            <div className="space-y-6">
              <div className="bg-white border rounded-lg p-4 sm:p-5 shadow-sm">
                <h3 className="font-semibold text-[#004172] mb-4">
                  Upload Prescription
                </h3>
                {loadingFiles ? (
                  <div className="flex h-32 sm:h-40 items-center justify-center">
                    <p className="text-gray-500">Loading existing files...</p>
                  </div>
                ) : !reportFile && !existingPrescription ? (
                  <label className="flex h-32 sm:h-40 items-center justify-center border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100">
                    <div className="text-center">
                      <img src={uploadicon} className="mx-auto mb-2 w-8" />
                      <p>Click to Upload Prescription</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-baseline sm:space-x-2 truncate">
                        <span className="font-medium text-gray-800 truncate">
                          📄{" "}
                          {reportFile?.displayName ||
                            existingPrescription?.displayName ||
                            "Prescription"}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openPreview(null, true)}
                          className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={removePrescription}
                          className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
{/* 
                    <label className="flex h-20 items-center justify-center border-dashed border-gray-300 rounded-lg hover:bg-gray-100 cursor-pointer">
                      <p>Click to Replace Prescription</p>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label> */}
                  </div>
                )}
              </div>

              <div className="bg-white border rounded-lg p-4 sm:p-5 shadow-sm">
                <h3 className="font-semibold text-[#004172] mb-4">
                  Upload Lab Reports
                </h3>
                <label className="flex h-32 sm:h-40 items-center justify-center border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100 mb-4">
                  <div className="text-center">
                    <img src={uploadicon} className="mx-auto mb-2 w-8" />
                    <p>Click to Upload Lab Reports</p>
                    <p className="text-xs text-gray-500">
                      You can select multiple files
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={handleLabReportUpload}
                    className="hidden"
                  />
                </label>
                {(labReportFiles.length > 0 ||
                  existingLabReports.length > 0) && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    <h4 className="font-medium text-[#004172] mb-2">
                      Uploaded Lab Reports (
                      {labReportFiles.length + existingLabReports.length})
                    </h4>
                    {/* Show existing lab reports */}
                    {existingLabReports.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-xl shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:space-x-2 truncate">
                          <span className="font-medium text-gray-800 truncate ">
                            🧪 {r.displayName || r.name}
                          </span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openPreview(r)}
                            className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeLabReport(r.id)}
                            className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {/* Show new lab reports */}
                    {labReportFiles.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-xl shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:space-x-2 truncate">
                          <span className="font-medium text-gray-800 truncate ">
                            🧪 {r.displayName || r.name}
                          </span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openPreview(r)}
                            className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeLabReport(r.id)}
                            className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8 md:mt-10">
            <button
              onClick={handleNext}
              disabled={isLoggingIn}
              className="w-full sm:w-[335px] py-2 rounded-full text-white bg-gradient-to-r from-[#004172] to-[#3cba84]"
            >
              {isLoggingIn ? "Loading..." : "Next"}
            </button>
          </div>
        </div>
      </>
    </>
  );
}
